import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { Dropdown } from "elements";

import moment from 'moment';
import styled from 'styled-components';

import useDeviceType from "../../../Hooks/ResponsiveHook";

import CongratsModal from "components/CongratsModal";
import StepLayout from "../StepLayout/index";
import Group from "./Group/index";

import logo from "assets/images/header_logo_modern.svg";
import SmallLogo from "assets/images/auth/logoGPS.svg";

import MagnifierPlusIcon from "assets/svg/magnifier-plus.svg";
import MagnifierMinusIcon from "assets/svg/magnifier-minus.svg";

import { getCookie } from "utils/StorageVariables";
import { ACTIONS_PATH } from "utils/routes";

import { RetrieveMasterPlanDetails } from "Redux/RetrieveMasterPlan/action";

import printIconOrange from "assets/images/journeys/print-icon-orange.svg";
import arrowDownIcon from "assets/images/journeys/arrow-down-icon-orange.svg";

// TODO:
// import LockedOver from "components/LockedOver/LockedOver";

import * as Styles from "./styled.js";
import styles from "./styles.module.scss";

import { track } from 'utils/amplitude';

// ---------------------------------------------------------------------------
// iOS (WebKit) whole-page printing
//
// Desktop Chrome/Firefox print the isolated react-to-print iframe, but iOS
// Safari/Chrome print the whole parent page instead. Worse, on iOS
// `window.print()` does not block, so react-to-print fires `onAfterPrint`
// immediately and the old approach (a transient .print-master-plan class
// removed in onAfterPrint) lost its marker before iOS rasterised the page -
// that is why the app header/footer/chat icon kept showing up.
//
// Fix: on iOS we clone the master plan into a dedicated #mpPrintOverlay that
// becomes the ONLY visible top-level node while printing (see pageStyles), and
// we keep the .print-master-plan marker mounted until the native print sheet
// actually closes (afterprint / print media-query change), never removing it in
// onAfterPrint.
// ---------------------------------------------------------------------------

const PRINT_OVERLAY_ID = "mpPrintOverlay";
const PRINT_CLASS = "print-master-plan";

// Detects iPhones, iPads and iPods (including Chrome on iOS, whose UA contains
// the iOS device token plus "CriOS"). Desktop browsers - and therefore the
// working react-to-print iframe print path - keep using react-to-print.
const IS_IOS = (() => {
  if (typeof navigator === "undefined" || typeof window === "undefined") {
    return false;
  }
  const ua = navigator.userAgent || "";
  if (/iP(hone|ad|od)/i.test(ua)) return true;
  if (/CriOS/i.test(ua)) return true; // Chrome on iOS
  return false;
})();

// Holds the teardown for the currently active iOS print session (if any).
let printSessionTeardown = null;

// Removes the print-only marker/overlay/listeners. Safe to call at any time.
const teardownIOSPrint = () => {
  if (printSessionTeardown) {
    printSessionTeardown();
    printSessionTeardown = null;
  }
  document.documentElement.classList.remove(PRINT_CLASS);
  document.body.classList.remove(PRINT_CLASS);
  const overlay = document.getElementById(PRINT_OVERLAY_ID);
  if (overlay && overlay.parentNode) {
    overlay.parentNode.removeChild(overlay);
  }
};

// Builds a print-only clone of the master plan and keeps it (plus the
// .print-master-plan marker) mounted until the iOS print sheet closes.
const setupIOSPrint = (sourceNode) => {
  teardownIOSPrint();

  if (!sourceNode) return;

  const overlay = document.createElement("div");
  overlay.id = PRINT_OVERLAY_ID;
  overlay.appendChild(sourceNode.cloneNode(true));
  document.body.appendChild(overlay);

  document.documentElement.classList.add(PRINT_CLASS);
  document.body.classList.add(PRINT_CLASS);

  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    teardownIOSPrint();
  };

  // Fired by WebKit once the native print sheet is closed (Print or Cancel).
  window.addEventListener("afterprint", finish);

  // Belt & braces for engines that only expose the print media-query change.
  const printMql =
    typeof window.matchMedia === "function"
      ? window.matchMedia("print")
      : null;
  const onPrintMqlChange = (e) => {
    if (!e.matches) finish();
  };
  if (printMql) {
    if (typeof printMql.addEventListener === "function") {
      printMql.addEventListener("change", onPrintMqlChange);
    } else if (typeof printMql.addListener === "function") {
      printMql.addListener(onPrintMqlChange);
    }
  }

  // Last-resort safety net so the DOM marker/overlay are never left behind.
  // Kept generous (5 min) so a slow user who keeps the iOS print sheet open is
  // still served by the print-only overlay when they finally hit "Print".
  const fallbackTimer = setTimeout(finish, 5 * 60 * 1000);

  printSessionTeardown = () => {
    window.removeEventListener("afterprint", finish);
    if (printMql) {
      if (typeof printMql.removeEventListener === "function") {
        printMql.removeEventListener("change", onPrintMqlChange);
      } else if (typeof printMql.removeListener === "function") {
        printMql.removeListener(onPrintMqlChange);
      }
    }
    clearTimeout(fallbackTimer);
  };
};

const MasterPlan = () => {
  const $componentRef = useRef();

  const [isPrintAll, setIsPrintAll] = useState(0);

  const handlePrint = useReactToPrint({
    documentTitle: "Mission Statement",
    onBeforePrint: () => {
      // Desktop browsers print the isolated react-to-print iframe, so the
      // class here only matters for the master plan's own @media print CSS.
      // On iOS the marker (and a print-only overlay) is set up by
      // setupIOSPrint() and must survive until the native print sheet closes,
      // so it is intentionally NOT removed in onAfterPrint.
      document.documentElement.classList.add(PRINT_CLASS);
      document.body.classList.add(PRINT_CLASS);

      if (IS_IOS) {
        setupIOSPrint($componentRef.current);
      }
    },
    onAfterPrint: () => {
      // On desktop print() blocks until the dialog closes, so we can clean up
      // right away. On iOS print() returns immediately and the sheet is still
      // open here; the .print-master-plan marker/overlay are torn down only
      // when the sheet closes (see setupIOSPrint).
      if (!IS_IOS) {
        teardownIOSPrint();
      }
      setIsPrintAll(0);
    },
    content: () => $componentRef.current,
  });

  const [masterPlanData, setMasterPlanData] = useState([]);
  const [zoomedIn, setZoomedIn] = useState(false);

  const toggleZoom = () => {
    setZoomedIn(!zoomedIn);
  };

  const dispatch = useDispatch();
  const domainName = getCookie("domainName");
  const selectedClient = getCookie("selectedClient");

  useEffect(() => {
    let MasterPlanId = getCookie("MasterPlanId");
    dispatch(RetrieveMasterPlanDetails(MasterPlanId));
  }, []);

  const { masterPlanDetails } = useSelector(
    (state) => state.RetrieveMasterPlanDetailsReducer
  );

  useEffect(() => {
    if (!masterPlanDetails?.areas.filter(area => !area.goal?.completed))
      return;

    const res = SortArr(masterPlanDetails?.areas.filter(area => !area.goal?.completed));
    setMasterPlanData(res);

    // Amplitude
    track("Master Plan Viewed", {
      journey: masterPlanDetails?.journey?.name,
    });
  }, [masterPlanDetails?.areas]);

  useEffect(() => {
    if (isPrintAll) {
      handlePrint();
    }
  }, [isPrintAll]);

  // If the user leaves this page while an iOS print sheet is open, remove the
  // print-only marker/overlay so they never leak into other screens.
  useEffect(() => {
    return () => {
      teardownIOSPrint();
    };
  }, []);

  const SortArr = (data) =>
    data?.sort(function (a, b) {
      return a?.goal?.priority - b?.goal?.priority;
    });

  const navigate = useNavigate();

  const handlePrev = () => {
    navigate(ACTIONS_PATH);
  };

  const handleNext = () => {
    navigate("/final-step");

    // Amplitude
    track("Master Plan Saved", {
      journey: masterPlanDetails?.journey?.name,
    });
  };

  const stepsItems = [
    {
      key: "summary",
      element: "Summary View",
    },
    {
      key: "detailed",
      element: "Detailed View",
    },
  ];

  const handlePrintClick = (step) => {
    if (step.key === "summary") {
      setIsPrintAll(1);
    } else {
      setIsPrintAll(2);
    }
  };

  const PrintDropdown = () => {
    return (
      <Dropdown
        menuItems={stepsItems}
        secondary
        overlayClassName={styles.custom}
        onChange={handlePrintClick}
      >
        <Styles.PrintToggler>
          <img src={printIconOrange} alt="" />
          Print
          <img src={arrowDownIcon} alt="" />
        </Styles.PrintToggler>
      </Dropdown>
    );
  }

  const pageStyles = printAll => {
    return `
      /*
        #mpPrintOverlay only exists during an iOS print (see setupIOSPrint).
        Keep it hidden on screen so we never show a duplicate master plan, and
        only reveal it inside @media print below.
      */
      #mpPrintOverlay {
        display: none !important;
      }

      @page {
        size: portrait;
        margin-top: 24px;
        margin-bottom: 0;
      }

      @page :first {
        margin-top: 0;
        margin-bottom: 50px;
      }
      
      @media print {
        .extra {
          display: ` + (printAll === 2 ? "flex" : "none") + `;
        }
        
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        * {
          box-sizing: border-box;
        }

        /*
          Fallback for iOS (WebKit), which cannot print the isolated
          react-to-print iframe and instead prints the whole parent page. On iOS
          the master plan is cloned into #mpPrintOverlay (see setupIOSPrint);
          while the .print-master-plan marker is present we make that overlay
          the ONLY visible top-level node (hiding the app header/footer/
          sidebar/chat icon) and neutralise the fixed-height/overflow-hidden app
          containers so the table reflows and paginates like a document.

          Desktop browsers print the isolated iframe (which never carries the
          marker), so these rules do not affect the regular desktop print path.
        */
        html.print-master-plan,
        html.print-master-plan body {
          height: auto !important;
          min-height: 0 !important;
          max-height: none !important;
          overflow: visible !important;
        }

        body.print-master-plan > *:not(#mpPrintOverlay) {
          display: none !important;
        }

        body.print-master-plan #mpPrintOverlay {
          display: block !important;
        }
      }
    `;
  };

  const screenSizes = useDeviceType();

  const isMobile = screenSizes === "mobile";
  const isTablet = screenSizes === "iPad";

  return (
    <StepLayout
      title="Master Plan"
      nextPageTitle="Save"
      footerLeft={<PrintDropdown />}
      smallerFooter
      onPrev={handlePrev}
      onNext={handleNext}
    >
      <Styles.Container>
        <Styles.ContainerItem>
          <TableContainer zoomedIn={zoomedIn}>
            <Styles.TableItem id="masterPlanPrintArea" ref={$componentRef}>
              <style>{pageStyles(isPrintAll)}</style>

              <Styles.TableHeader>
                <Styles.HeaderItem>
                  <Styles.Logo src={isMobile || isTablet ? SmallLogo : logo} alt="logo" />
                  <Styles.Span></Styles.Span>
                  <Styles.Heading>
                    {domainName || ""} Goal Setting by Arootah
                  </Styles.Heading>
                </Styles.HeaderItem>
                <Styles.HeaderItemRight>
                  <Styles.Title>Master Plan</Styles.Title>
                </Styles.HeaderItemRight>
              </Styles.TableHeader>

              {masterPlanDetails?.mission_statement ? <Styles.Journey>
                <Styles.JourneyTitle>Journey Mission Statement</Styles.JourneyTitle>
                <Styles.JourneyText>{masterPlanDetails?.mission_statement}</Styles.JourneyText>
              </Styles.Journey> : null}

              <Styles.TableBody>
                {masterPlanData && masterPlanData?.map((item, index) => (
                  <Group data={item} index={index} renderAll />
                ))}
              </Styles.TableBody>

              <Styles.FooterText id="footer">
                Copyright &copy; Arootah {moment().year()}. All Rights Reserved
              </Styles.FooterText>
            </Styles.TableItem>
          </TableContainer>
        </Styles.ContainerItem>
      </Styles.Container>

      <Styles.Magnifier onClick={toggleZoom}>
        <Styles.MagnifierIcon src={zoomedIn ? MagnifierMinusIcon : MagnifierPlusIcon} />
      </Styles.Magnifier>

      <CongratsModal isActive={!selectedClient && masterPlanDetails && !masterPlanDetails.viewed_masterplan_screen} />
    </StepLayout>
  );
};

export default MasterPlan;

export const TableContainer = styled.div`
  padding: 12px;
  width: 100%;
  height: 100%;
  overflow: auto;
  scroll-behavior: smooth;
  position: relative;

  @media screen and (max-width: 743px) {
    zoom: ${(props) => (props.zoomedIn ? 'scale(1.3)' : 'scale(1)')};
    width: ${(props) => (props.zoomedIn ? '130%' : '100%')};
    margin: 10px 0 0 0;
    overflow: scroll;
    padding: 0;
    transition: transform 0.3s ease;
  }
  
  @media print {
    overflow: visible;
    height: auto;
    padding: 0;
    margin: 0;
  }

  ${({ theme }) => theme.min("sm")`
    ::-webkit-scrollbar {
      width: 11px;
    }

    /* Track */
    ::-webkit-scrollbar-track {
      box-shadow: inset 0 0 5px #71aaca;
      border-radius: 10px;
      background: #71aaca;
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
      background: #116595;
      border-radius: 10px;
    }

    /* Handle on hover */
    ::-webkit-scrollbar-thumb:hover {
      background: #116595;
    }
  `}
`;
